import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractCRUD, BannerNotificationsService, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { catchError } from 'rxjs/operators';
import { AuditResource, BoardSeat } from '../../shared/models';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { CONFIG } from 'src/app/config';

@Injectable({
  providedIn: 'root'
})
export class BoardSeatService extends AbstractCRUD<BoardSeat> implements CRUDService<BoardSeat> {

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService
  ) {
    super(exceptionService, http, utilsService);
  }

  public getResourcePath(): string {
    return '/restricted-list/boardseats';
  }

  public getBoardSeats(params: any): Observable<PaginatedList<BoardSeat>> {
    return super.getHTTP().get(`${this.getApiPath()}/all`, {params: params})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createBoardSeat(body: any): Observable<BoardSeat> {
    return super.getHTTP().post(`${this.getApiPath()}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateBoardSeat(id: number, body: any): Observable<BoardSeat> {
    return super.getHTTP().put(`${this.getApiPath()}/${id}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateBoardSeatEndDate(id: number, body: any): Observable<BoardSeat> {
    return super.getHTTP().put(`${this.getApiPath()}/${id}/end-date`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public deleteBoardSeat(id: number, endDate?: DateTime, forceEzeError = false): Observable<BoardSeat> {
    return super.getHTTP().put(`${this.getApiPath()}/delete/${id}`, {
        "endDate": endDate?.toUTC().toFormat(CONFIG.dateTimeServerFormatFullMillis),
        forceEzeError
      })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getBoardSeatAudit(body: any): Observable<PaginatedList<AuditResource>> {
    return super.getHTTP().get(`${this.getApiPath()}/audit`, {params: body})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public exportBoardSeats(filter: any) {
    filter.page = 0;
    filter.pagesize = 9999999;
    return super.getHTTP().get(`${this.getApiPath()}/export`, {params: filter, responseType: 'text'})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)))
      .subscribe((value: any) => {
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:attachment/csv,' + encodeURI(value);
          hiddenElement.target = '_blank';
          hiddenElement.download = 'board_seats.csv';
          document.body.appendChild(hiddenElement);
          hiddenElement.click();
          this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        }, () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  public exportBoardSeatAudit(filter: any) {
    filter.page = 0;
    filter.pagesize = 9999999;
    return super.getHTTP().get(`${this.getApiPath()}/audit/export`, {params: filter, responseType: 'text'})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)))
      .subscribe((value: any) => {
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:attachment/csv,' + encodeURI(value);
          hiddenElement.target = '_blank';
          hiddenElement.download = 'board_seat_audit.csv';
          document.body.appendChild(hiddenElement);
          hiddenElement.click();
          this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        }, () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  public getBoardSeatExist(companyIds: Array<number>, params?: any) {
    return this.getHTTP().get(`${this.getApiPath()}/exist`, { params: {...params, companyId: companyIds } } );
  }
}
